package fr.frux.feature.treedetail

import fr.frux.core.common.error.asAppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.designsystem.components.UserModerationMessage
import fr.frux.core.designsystem.components.UserReportReason
import fr.frux.shared.domain.repository.TreeRepository
import fr.frux.shared.domain.repository.UserRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private const val TAG = "TreeDetailModeration"
private const val CONTENT_REPORT_KIND = "INAPPROPRIATE_CONTENT"

/**
 * Gestes de l'ADR 0029 (#515) sur la description et l'auteur de la fiche arbre, extraits de
 * [TreeDetailViewModel] (ratchet detekt LargeClass) — même patron que `LocationController` et
 * `NearestDistanceLoader` dans `MapViewModel`.
 */
internal class TreeDetailModerationController(
    private val treeId: Long,
    private val treeRepository: TreeRepository,
    private val userRepository: UserRepository?,
    private val scope: CoroutineScope,
    private val state: MutableStateFlow<TreeDetailState>,
    private val requireLogin: (TreeDetailLoginPrompt) -> Boolean,
) {
    /** Vrai si un compte est connecté, sinon pose l'invite (#264). L'écran l'appelle avant
     *  d'ouvrir la feuille de signalement ou la confirmation de masquage. */
    fun requestModeration(): Boolean = requireLogin(TreeDetailLoginPrompt.Moderate)

    /** Signale la description (ADR 0029) : file de triage, aucun effet visible sur la fiche. */
    fun onReportDescriptionClick() {
        val tree = state.value.tree ?: return
        if (!requireLogin(TreeDetailLoginPrompt.Moderate) || !canReportDescription(tree)) return
        run(onSuccess = UserModerationMessage.ContentReported) {
            treeRepository.reportTree(treeId, kind = CONTENT_REPORT_KIND).map { }
        }
    }

    private fun canReportDescription(tree: TreeDetail): Boolean =
        !tree.isOwnTree && !tree.description.isNullOrBlank() && !state.value.isModerationPending

    /** Signale l'auteur au back-office. [reason] voyage par son `name` (code serveur). */
    fun onReportCreator(
        reason: UserReportReason,
        details: String?,
    ) {
        val tree = state.value.tree ?: return
        val users = userRepository
        if (users == null || !requireLogin(TreeDetailLoginPrompt.Moderate) || !canActOnCreator(tree)) return
        run(onSuccess = UserModerationMessage.UserReported) {
            users.reportUser(tree.createdBy, reason.name, details)
        }
    }

    /** Masque les contributions de l'auteur pour soi (ADR 0029). L'écran revient ensuite à la carte. */
    fun onBlockCreatorConfirmed() {
        val tree = state.value.tree ?: return
        val users = userRepository
        if (users == null || !requireLogin(TreeDetailLoginPrompt.Moderate) || !canActOnCreator(tree)) return
        run(onSuccess = UserModerationMessage.Blocked(tree.creatorUsername.orEmpty())) {
            users.blockUser(tree.createdBy)
        }
    }

    private fun canActOnCreator(tree: TreeDetail): Boolean = tree.canModerateCreator && !state.value.isModerationPending

    fun consumeModerationMessage() {
        state.update { it.copy(moderationMessage = null) }
    }

    private fun run(
        onSuccess: UserModerationMessage,
        call: suspend () -> Result<Unit>,
    ) {
        state.update { it.copy(isModerationPending = true, moderationMessage = null) }
        scope.launch {
            val message =
                call().fold(
                    onSuccess = { onSuccess },
                    onFailure = { error ->
                        Logger.e(TAG, "❌ Geste de modération échoué", error)
                        UserModerationMessage.Failed(error.asAppError())
                    },
                )
            state.update { it.copy(isModerationPending = false, moderationMessage = message) }
        }
    }
}
